<template>
  <div class="upload-page">
    <div class="upload-card">
      <div class="upload-card__header">
        <div class="header__icon">📄</div>
        <div>
          <h2 class="header__title">FormIQ 2 · AutoFill</h2>
          <p class="header__sub">Dokument hochladen, um das Antragsformular automatisch auszufüllen</p>
        </div>
        <div class="header__badge">Schritt 1 von 4</div>
      </div>

      <div
        class="upload-zone"
        :class="{
          'upload-zone--dragover': isDragOver,
          'upload-zone--uploaded': uploadedFile,
          'upload-zone--processing': isProcessing,
        }"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
        @drop.prevent="handleDrop"
        @click="!uploadedFile && $refs.fileInput.click()"
      >
        <input
          ref="fileInput"
          type="file"
          accept=".pdf,.docx,.png,.jpg,.jpeg,.xml,.xls,.xlsx"
          style="display:none"
          @change="handleFileChange"
        />

        <!-- Processing state -->
        <template v-if="isProcessing">
          <div class="upload-zone__processing">
            <div class="spinner"></div>
            <p class="processing__title">Dokument wird analysiert…</p>
            <p class="processing__sub">FormIQ 2 extrahiert die Felddaten</p>
            <div class="processing__bar">
              <div class="processing__fill" :style="{ width: progressWidth + '%' }"></div>
            </div>
            <p class="processing__pct">{{ Math.round(progressWidth) }}%</p>
          </div>
        </template>

        <!-- File uploaded (ready) -->
        <template v-else-if="uploadedFile">
          <div class="upload-zone__done">
            <span class="done__check">✓</span>
            <p class="done__name">{{ uploadedFile.name }}</p>
            <p class="done__hint">Bereit zur Extraktion. Klicken Sie auf „AutoFill starten".</p>
            <button class="done__change" @click.stop="clearFile">Datei ändern</button>
          </div>
        </template>

        <!-- Empty state -->
        <template v-else>
          <div class="upload-zone__empty">
            <span class="empty__icon">📁</span>
            <p class="empty__title">Dokument hier ablegen</p>
            <p class="empty__sub">oder klicken zum Durchsuchen</p>
            <p class="empty__types">Unterstützt: PDF, DOCX, PNG, JPG, XML, XLS, XLSX</p>
          </div>
        </template>
      </div>

      <!-- Accepted docs info -->
      <div class="upload-info">
        <div class="info-item">
          <span>🪪</span>
          <div>
            <strong>Vollmacht</strong>
            <span>Name der Vertretung, Daten, Kontaktangaben</span>
          </div>
        </div>
        <div class="info-item">
          <span>📜</span>
          <div>
            <strong>Zertifikat / Ausweis</strong>
            <span>Persönliche Daten für automatisches Fellauffüllen</span>
          </div>
        </div>
      </div>

      <div v-if="errorMessage" class="upload-error">
        <span>⚠</span> {{ errorMessage }}
      </div>

      <div class="upload-actions">
        <button
          class="action-btn action-btn--skip"
          @click="$emit('skip')"
        >
          Überspringen – manuell ausfüllen
        </button>
        <button
          class="action-btn action-btn--fill"
          :disabled="!uploadedFile || isProcessing"
          @click="runAutoFill"
        >
          <span>✦</span> AutoFill starten
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import config from '../config.js';
import { authFetch } from '../utils/apiClient.js';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 40;

export default {
  name: 'AutoFillUpload',
  props: {
    clientId: { type: String, default: 'form2_einbuergerung' },
  },
  emits: ['autofill-done', 'skip'],

  setup(props, { emit }) {
    const fileInput = ref(null);
    const uploadedFile = ref(null);
    const isDragOver = ref(false);
    const isProcessing = ref(false);
    const progressWidth = ref(0);
    const errorMessage = ref(null);

    const handleFileChange = (e) => {
      const f = e.target.files[0];
      if (f) { uploadedFile.value = f; errorMessage.value = null; }
    };

    const handleDrop = (e) => {
      isDragOver.value = false;
      const f = e.dataTransfer.files[0];
      if (f) { uploadedFile.value = f; errorMessage.value = null; }
    };

    const clearFile = () => {
      uploadedFile.value = null;
      errorMessage.value = null;
      if (fileInput.value) fileInput.value.value = '';
    };

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const pollExtraction = async (documentId) => {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        await sleep(POLL_INTERVAL_MS);
        const resp = await authFetch(
          `${config.backendUrl}/v2/documents/${props.clientId}/document/${documentId}`
        );
        if (!resp.ok) throw new Error(`Polling failed: HTTP ${resp.status}`);
        const body = await resp.json();

        progressWidth.value = Math.min(30 + ((attempt + 1) / MAX_POLL_ATTEMPTS) * 65, 95);

        if (body.status === 'EXTRACTED' || body.status === 'COMPLETE') return body.data;
        if (body.status === 'FAILED') throw new Error('Document extraction failed on the server.');
      }
      throw new Error('Extraction timed out after 2 minutes.');
    };

    const runAutoFill = async () => {
      isProcessing.value = true;
      progressWidth.value = 5;
      errorMessage.value = null;

      try {
        const formData = new FormData();
        formData.append('CV', uploadedFile.value);

        const uploadResp = await authFetch(
          `${config.backendUrl}/v2/documents/upload/${props.clientId}`,
          { method: 'POST', body: formData }
        );
        if (!uploadResp.ok) {
          const detail = await uploadResp.json().catch(() => ({}));
          throw new Error(detail?.detail?.message || `Upload failed: HTTP ${uploadResp.status}`);
        }
        const documentId = await uploadResp.json();
        progressWidth.value = 25;

        const extractedData = await pollExtraction(documentId);
        progressWidth.value = 100;

        setTimeout(() => {
          isProcessing.value = false;
          emit('autofill-done', extractedData);
        }, 400);
      } catch (e) {
        isProcessing.value = false;
        progressWidth.value = 0;
        errorMessage.value = e.message || 'An unexpected error occurred.';
      }
    };

    return {
      fileInput, uploadedFile, isDragOver, isProcessing, progressWidth, errorMessage,
      handleFileChange, handleDrop, clearFile, runAutoFill,
    };
  },
};
</script>

<style scoped>
.upload-page {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  background: #f0f2f8;
}

.upload-card {
  background: #fff;
  border-radius: 20px;
  padding: 36px;
  max-width: 580px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
}

.upload-card__header {
  display: flex;
  align-items: center;
  gap: 16px;
}
.header__icon { font-size: 36px; flex-shrink: 0; }
.header__title {
  font-size: 18px;
  font-weight: 800;
  color: #1a1a2e;
}
.header__sub { font-size: 13px; color: #666; margin-top: 2px; }
.header__badge {
  margin-left: auto;
  background: #f1f5f9;
  color: #1e293b;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

.upload-zone {
  border: 2px dashed #d1d5db;
  border-radius: 16px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #fafafa;
}
.upload-zone--dragover {
  border-color: #1e293b;
  background: #f8fafc;
}
.upload-zone--uploaded {
  border-color: #10b981;
  background: #f0fdf4;
  cursor: default;
}
.upload-zone--processing {
  border-color: #1e293b;
  background: #f8fafc;
  cursor: default;
}

.upload-zone__empty,
.upload-zone__done,
.upload-zone__processing {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px;
  text-align: center;
}

.empty__icon { font-size: 40px; }
.empty__title { font-size: 15px; font-weight: 700; color: #374151; }
.empty__sub { font-size: 13px; color: #9ca3af; }
.empty__types {
  font-size: 11px;
  color: #d1d5db;
  background: #f3f4f6;
  padding: 4px 12px;
  border-radius: 20px;
  margin-top: 4px;
}

.done__check {
  width: 52px;
  height: 52px;
  background: #10b981;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
}
.done__name { font-size: 14px; font-weight: 700; color: #065f46; }
.done__hint { font-size: 12px; color: #6b7280; }
.done__change {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #1e293b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.processing__title { font-size: 14px; font-weight: 700; color: #1a1a2e; }
.processing__sub { font-size: 12px; color: #9ca3af; }

.processing__bar {
  width: 200px;
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
}
.processing__fill {
  height: 100%;
  background: linear-gradient(90deg, #1e293b, #475569);
  border-radius: 3px;
  transition: width 0.2s;
}
.processing__pct { font-size: 12px; color: #1e293b; font-weight: 700; }

.upload-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.info-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f9fafb;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 22px;
}
.info-item div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.info-item strong { font-size: 13px; color: #374151; font-weight: 700; }
.info-item span { font-size: 12px; color: #9ca3af; }

.upload-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: #dc2626;
  font-weight: 500;
}

.upload-actions {
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.action-btn {
  flex: 1;
  padding: 13px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.action-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
.action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.action-btn--skip {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}
.action-btn--fill {
  background: linear-gradient(135deg, #1e293b, #334155);
  color: #fff;
}
</style>
